import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook } from 'lucide-react';
import { navigation, companyInfo } from '../../data.js';

export default function Footer() {
  useEffect(() => {
    const loader = () => {
      // Undgå at tilføje scriptet flere gange
      if (document.querySelector('script[src="https://cdn.iubenda.com/iubenda.js"]')) {
        return;
      }
      const s = document.createElement("script");
      s.type = "text/javascript";
      s.src = "https://cdn.iubenda.com/iubenda.js";
      const tag = document.getElementsByTagName("script")[0];
      if (tag && tag.parentNode) {
        tag.parentNode.insertBefore(s, tag);
      } else {
        document.body.appendChild(s);
      }
    };

    if (window.addEventListener) {
      window.addEventListener("load", loader, false);
    } else if (window.attachEvent) {
      window.attachEvent("onload", loader);
    } else {
      window.onload = loader;
    }

    // Kør indlæseren med det samme, hvis dokumentet allerede er indlæst (vigtigt i React SPAs)
    if (document.readyState === "complete") {
      loader();
    }
  }, []);

  return (
    <footer className="bg-slate-900 pt-20 pb-10 text-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-white">Sydthy Taxi & Turistfart</h3>
            <p className="max-w-xs text-slate-400 leading-relaxed">
              Professionel transportløsning i Thy og på Mors. Vi forener lokal tryghed med moderne komfort.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/profile.php?id=100090401324788" 
                target="_blank" 
                rel="noopener noreferrer"
                className="rounded-full bg-white/5 p-2 transition-colors hover:bg-blue-600"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="mb-6 text-sm font-semibold uppercase tracking-wider text-white">Menu</h3>
            <ul className="space-y-4">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="text-slate-400 transition-colors hover:text-white"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="mb-6 text-sm font-semibold uppercase tracking-wider text-white">Kontakt</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 text-slate-400 transition-colors hover:text-white"
                >
                  <Phone className="h-4 w-4 text-blue-500" />
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-slate-400 transition-colors hover:text-white"
                >
                  <Mail className="h-4 w-4 text-blue-500" />
                  {companyInfo.email}
                </a>
              </li>
              <li className="pt-2 border-t border-white/5">
                <a 
                  href="https://www.iubenda.com/privacy-policy/70001933" 
                  className="iubenda-black iubenda-noiframe iubenda-embed text-slate-400 hover:text-white transition-colors text-sm block" 
                  title="Privatlivspolitik "
                >
                  Privatlivspolitik
                </a>
              </li>
              <li>
                <a 
                  href="https://www.iubenda.com/privacy-policy/70001933/cookie-policy" 
                  className="iubenda-black iubenda-noiframe iubenda-embed text-slate-400 hover:text-white transition-colors text-sm block" 
                  title="Cookiepolitik "
                >
                  Cookiepolitik
                </a>
              </li>
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="mb-6 text-sm font-semibold uppercase tracking-wider text-white">Adresser</h3>
            <ul className="space-y-6">
              {companyInfo.locations.map((loc, idx) => (
                <li key={idx} className="flex gap-3 text-slate-400">
                  <MapPin className="h-4 w-4 shrink-0 text-blue-500" />
                  <div>
                    <p className="text-white font-medium">{loc.address}</p>
                    <p className="text-sm">{loc.city}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-20 border-t border-white/5 pt-10 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} {companyInfo.name}. CVR: {companyInfo.cvr}. Alle rettigheder forbeholdes.</p>
        </div>
      </div>
    </footer>
  );
}
